import React, { useState, useEffect } from "react";
import { FaUserMd, FaStethoscope, FaUser, FaPhoneAlt, FaAmbulance, FaHeartbeat } from "react-icons/fa";
import { Globe, ChevronDown, ChevronUp, Menu, X, Notebook, Shield, Plus, Bed, FileText, ReceiptIndianRupee, HeartHandshake, ClipboardPlus  } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.jpg";

const Navbar = () => {
    const location = useLocation();
    const [openDropdown, setOpenDropdown] = useState(null);
    const [timeoutId, setTimeoutId] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [showEmergencyModal, setShowEmergencyModal] = useState(false);
    const [isBlinking, setIsBlinking] = useState(true);

    const emergencyContact = {
        phone: "+91 0542-2366187",
        email: "osdtraumabhu@gmail.com"
    };

    const menuItems = [
        {
            title: "About Us",
            dropdown: [
                { label: "Overview", link: "/overview" },
                { label: "Our Vision & Mission", link: "/vision-mission" },
                { label: "Quality of Care Certifications", link: "/quality" },
            ],
        },
        {
            title: "Infra & Technology",
            link: "/infrastructure",
        },
        {
            title: "Ayushman Bharat",
            link: "/ayushman-bharat",
        },
        {
            title: "Media & Information",
            link: "/media-and-information",
        },
        {
            title: "Contact Us",
            dropdown: [
                { label: "Contact Us", link: "/contact-us" },
            ],
        },
    ];

    // Forms Menu Items
    const formsMenuItems = [
    {
        title: "मृत्यु प्रमाण पत्र",
        subtitle: "Death Certificate",
        link: "/forms/death-certificate",
        icon: Bed  // 💀 Death/Deceased के लिए
    },
    {
        title: "उपचार का अनुमानित लागत",
        subtitle: "Treatment Cost Estimate",
        link: "/forms/treatment-estimate",
        icon: ReceiptIndianRupee  // 💰 Cost/Money के लिए
    },
    {
        title: "जीवन बीमा दावा प्रपत्र",
        subtitle: "Life Insurance Claim",
        link: "/forms/life-insurance-claim",
        icon: HeartHandshake  // 🤝 Insurance/Care के लिए
    },
    {
        title: "चिकित्सा प्रमाण पत्र",
        subtitle: "Medical Certificate",
        link: "/forms/medical-certificate",
        icon: ClipboardPlus  // 📋 Medical Document के लिए
    },
];

    // Track scroll position for sticky navbar
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 10) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Close mobile menu when viewport size changes to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Handle blinking effect for Trauma Center Facilities button
    useEffect(() => {
        const interval = setInterval(() => {
            setIsBlinking((prev) => !prev);
        }, 800);
        return () => clearInterval(interval);
    }, []);

    // Handle mouse enter to open dropdown (desktop)
    const handleMouseEnter = (index) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        setOpenDropdown(index);
    };

    // Handle mouse leave to close dropdown with delay (desktop)
    const handleMouseLeave = () => {
        const id = setTimeout(() => {
            setOpenDropdown(null);
        }, 200);
        setTimeoutId(id);
    };

    // Handle click to toggle dropdown (mobile and desktop)
    const handleClick = (index) => {
        setOpenDropdown(openDropdown === index ? null : index);
    };

    // Toggle mobile menu
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        setOpenDropdown(null);
    };

    // Toggle emergency modal
    const toggleEmergencyModal = () => {
        setShowEmergencyModal(!showEmergencyModal);
    };

    // Handle emergency call
    const handleEmergencyCall = () => {
        window.location.href = `tel:${emergencyContact.phone}`;
    };

    // Special Trauma Center Button Component
    const TraumaCenterButton = ({ isMobile = false }) => {
        const gradientClasses = isBlinking
            ? "bg-gradient-to-r from-red-500 via-pink-500 to-red-600"
            : "bg-gradient-to-r from-teal-600 via-teal-600 to-blue-700";

        return (
            <Link
                to="/trauma/center/facility"
                onClick={isMobile ? toggleMobileMenu : undefined}
                className={`
                    relative inline-flex items-center justify-center
                    ${isMobile
                        ? 'w-full px-4 py-3 text-sm'
                        : 'px-3 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm lg:px-5 lg:py-2.5 lg:text-sm xl:px-6 xl:py-3 xl:text-base'
                    }
                    ${gradientClasses}
                    text-white font-semibold rounded-lg shadow-md
                    transform transition-all duration-300 ease-in-out
                    hover:scale-105 hover:shadow-lg active:scale-95
                    cursor-pointer group overflow-hidden
                    border-2 border-white/20
                    ${isBlinking ? 'animate-pulse' : ''}
                `}
            >
                <div className={`absolute inset-0 rounded-lg ${isBlinking ? 'animate-ping' : ''} bg-white opacity-10`}></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent 
                    opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full 
                    transition-all duration-700 ease-out -skew-x-12"></div>
                <div className="relative flex items-center justify-center gap-1.5 sm:gap-2">
                    <span className={`
                        font-semibold leading-tight
                        ${isMobile ? 'text-sm' : 'text-xs sm:text-sm lg:text-sm xl:text-base'}
                    `}>
                        {isMobile ? "Facilities Available in Trauma Centre" : "Facilities Available in Trauma Centre"}
                    </span>
                </div>
                <div className={`
                    absolute -top-1 -right-1 
                    ${isMobile ? 'w-3 h-3' : 'w-2 h-2 sm:w-3 sm:h-3'} 
                    bg-yellow-400 rounded-full border-2 border-white
                    ${isBlinking ? 'animate-pulse' : ''}
                `}></div>
            </Link>
        );
    };

    return (
        <>
            <header className={`fixed top-0 left-0 w-full z-50 bg-white ${scrolled ? 'shadow-lg' : ''}`}>
                {/* Top Navbar */}
                <div className="w-full flex items-center justify-between px-4 py-2 bg-white border shadow-md border-cyan-300 sm:px-6 md:px-8 lg:px-10 xl:px-12">
                    {/* Logo */}
                    <Link to="/" className="flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <img src={logo} alt="logo" className="h-10 sm:h-12 md:h-14" />
                        </div>
                    </Link>

                    {/* Hamburger Menu Button (Mobile) */}
                    <button
                        className="md:hidden focus:outline-none"
                        onClick={toggleMobileMenu}
                        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                    >
                        {isMobileMenuOpen ? (
                            <X className="w-6 h-6 text-[#004AAC]" />
                        ) : (
                            <Menu className="w-6 h-6 text-[#004AAC]" />
                        )}
                    </button>

                    {/* Top Navbar Links (Desktop) */}
                    <div className="hidden md:flex md:items-center md:gap-2 lg:gap-4 xl:gap-6 flex-grow justify-center">
                        {/* <Link to="/doctor">
                            <div
                                className={`flex items-center gap-1 cursor-pointer font-medium text-xs lg:text-sm xl:text-base ${location.pathname === "/doctor"
                                    ? "text-[#AD3DC5]"
                                    : "text-[#009CA6]"
                                    } hover:text-[#AD3DC5] transition-colors`}
                            >
                                <FaUserMd className="flex-shrink-0 text-xs md:text-sm" />
                                <span className="whitespace-nowrap">Our Doctors</span>
                            </div>
                        </Link> */}
                        <Link to="/medical-specialty">
                            <div
                                className={`flex items-center gap-1 cursor-pointer font-medium text-xs lg:text-sm xl:text-base ${location.pathname === "/medical-specialty"
                                    ? "text-[#AD3DC5]"
                                    : "text-[#009CA6]"
                                    } hover:text-[#AD3DC5] transition-colors`}
                            >
                                <FaStethoscope className="flex-shrink-0 text-xs md:text-sm" />
                                <span className="whitespace-nowrap">Medical Specialties</span>
                            </div>
                        </Link>
                        <Link to="/opd-Schedule">
                            <div
                                className={`flex items-center gap-1 cursor-pointer font-medium text-xs lg:text-sm xl:text-base ${location.pathname === "/opd-Schedule"
                                    ? "text-[#AD3DC5]"
                                    : "text-[#009CA6]"
                                    } hover:text-[#AD3DC5] transition-colors`}
                            >
                                <Notebook className="h-3 md:h-4 flex-shrink-0" />
                                <span className="whitespace-nowrap">OPD Schedule</span>
                            </div>
                        </Link>

                        {/* NEW: Forms Dropdown - Desktop */}
                        <div
                            className="relative cursor-pointer"
                            onMouseEnter={() => handleMouseEnter('forms')}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button
                                className={`flex items-center gap-1 font-medium text-xs lg:text-sm xl:text-base ${openDropdown === 'forms' ? "text-[#AD3DC5]" : "text-[#009CA6]"
                                    } hover:text-[#AD3DC5] transition-colors`}
                                onClick={() => handleClick('forms')}
                                aria-expanded={openDropdown === 'forms'}
                                aria-controls="forms-dropdown"
                            >
                                <FileText className="flex-shrink-0 text-xs md:text-sm" />
                                <span className="whitespace-nowrap">Online Form Request</span>
                                <ChevronDown className="w-3 h-3 lg:w-4 lg:h-4" />
                            </button>

                            <div
                                id="forms-dropdown"
                                className={`absolute left-0 z-50 w-64 py-2 mt-1 bg-white border rounded-lg shadow-lg transition-all duration-200 ${openDropdown === 'forms'
                                        ? "opacity-100 translate-y-0"
                                        : "opacity-0 translate-y-1 pointer-events-none"
                                    }`}
                                style={{
                                    left: '50%',
                                    transform: openDropdown === 'forms'
                                        ? 'translateX(-50%) translateY(0)'
                                        : 'translateX(-50%) translateY(1rem)'
                                }}
                            >
                                <ul className="text-sm text-black">
                                    {formsMenuItems.map((formItem, i) => (
                                        <li key={i}>
                                            <Link
                                                to={formItem.link}
                                                className={`block px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${location.pathname === formItem.link
                                                        ? "bg-blue-50 font-semibold text-[#004AAC]"
                                                        : ""
                                                    }`}
                                                onClick={() => setOpenDropdown(null)}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <formItem.icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <div className="font-medium">{formItem.title}</div>
                                                        <div className="text-xs text-gray-500">{formItem.subtitle}</div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <a href="https://kiosk.traumacentrebhu.com" target="_blank" rel="noopener noreferrer">
                            <div className={`flex items-center gap-1 cursor-pointer font-medium text-xs lg:text-sm xl:text-base text-[#0CA6A6] hover:text-[#AD3DC5] transition-colors`}>
                                <FaUser className="flex-shrink-0 text-xs md:text-sm" />
                                <span className="whitespace-nowrap">Patient Portal</span>
                            </div>
                        </a>

                        <Link to="/bed-availability">
                            <div
                                className={`
                                    relative inline-flex items-center justify-center
                                    px-2 py-1 text-xs lg:text-sm xl:text-base
                                    ${location.pathname === "/bed-availability"
                                        ? "bg-[#AD3DC5]"
                                        : isBlinking
                                            ? "bg-gradient-to-r from-red-500 via-pink-500 to-red-600"
                                            : "bg-gradient-to-r from-teal-600 via-teal-600 to-blue-700"
                                    }
                                    text-white font-semibold rounded-lg shadow-md
                                    transform transition-all duration-300 ease-in-out
                                    hover:scale-105 hover:shadow-lg active:scale-95
                                    cursor-pointer group overflow-hidden
                                    border-2 border-white/20
                                    ${isBlinking ? 'animate-pulse' : ''}
                                `}
                            >
                                <div className={`absolute inset-0 rounded-lg ${isBlinking ? 'animate-ping' : ''} bg-white opacity-10`}></div>
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent 
                                    opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full 
                                    transition-all duration-700 ease-out -skew-x-12"></div>
                                <div className="relative flex items-center justify-center gap-1">
                                    <Bed className="flex-shrink-0 text-xs md:text-sm" />
                                    <span className="font-semibold leading-tight whitespace-nowrap">Bed Availability</span>
                                </div>
                                <div className={`
                                    absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3
                                    bg-yellow-400 rounded-full border-2 border-white
                                    ${isBlinking ? 'animate-pulse' : ''}
                                `}></div>
                            </div>
                        </Link>
                    </div>

                    {/* Language and Emergency Button (Desktop) */}
                    <div className="hidden md:flex md:items-center md:gap-2 lg:gap-4 flex-shrink-0">
                        <button
                            className="flex items-center gap-1 bg-[#FF4F73] text-white px-2 py-1 md:px-3 md:py-1.5 lg:px-4 lg:py-2 rounded-lg shadow hover:bg-red-500 transition text-xs lg:text-sm whitespace-nowrap"
                            onClick={toggleEmergencyModal}
                        >
                            <FaPhoneAlt className="text-white text-xs" />
                            <span className="font-semibold">Emergency</span>
                        </button>
                    </div>
                </div>

                {/* Bottom Center Dropdown Menu (Desktop) */}
                <nav className="hidden md:block bg-gray-100">
                    <div className="flex flex-row justify-center items-center flex-wrap gap-2 md:gap-3 lg:gap-5 xl:gap-8 py-2 px-1 md:py-3 md:px-2">
                        {menuItems.map((item, index) => (
                            <div
                                key={index}
                                className="relative cursor-pointer"
                                onMouseEnter={() => handleMouseEnter(index)}
                                onMouseLeave={handleMouseLeave}
                            >
                                {item.link ? (
                                    <Link
                                        to={item.link}
                                        className={`flex items-center gap-1 font-medium text-xs lg:text-sm xl:text-base text-black ${location.pathname === item.link
                                            ? "font-bold text-[#004AAC]"
                                            : ""
                                            } hover:text-[#004AAC] transition-colors`}
                                    >
                                        {item.title}
                                    </Link>
                                ) : (
                                    <button
                                        className={`flex items-center gap-1 font-medium text-xs lg:text-sm xl:text-base ${openDropdown === index ? "text-[#004AAC]" : "text-black"
                                            } hover:text-[#004AAC] transition-colors`}
                                        onClick={() => handleClick(index)}
                                        aria-expanded={openDropdown === index}
                                        aria-controls={`dropdown-${index}`}
                                    >
                                        {item.title} <ChevronDown className="w-3 h-3 lg:w-4 lg:h-4" />
                                    </button>
                                )}

                                {item.dropdown && (
                                    <div
                                        id={`dropdown-${index}`}
                                        className={`absolute left-0 z-50 w-48 py-1 mt-1 bg-white border rounded-lg shadow-lg transition-all duration-200 ${openDropdown === index
                                            ? "opacity-100 translate-y-0"
                                            : "opacity-0 translate-y-1 pointer-events-none"
                                            }`}
                                        style={{
                                            left: '50%',
                                            transform: openDropdown === index
                                                ? 'translateX(-50%) translateY(0)'
                                                : 'translateX(-50%) translateY(1rem)'
                                        }}
                                    >
                                        <ul className="text-sm text-black">
                                            {item.dropdown.map((subItem, i) => (
                                                <li key={i}>
                                                    <Link
                                                        to={subItem.link}
                                                        className={`block px-4 py-2 hover:bg-gray-50 transition-colors ${location.pathname === subItem.link
                                                            ? "font-semibold text-[#004AAC]"
                                                            : ""
                                                            }`}
                                                        onClick={() => setOpenDropdown(null)}
                                                    >
                                                        {subItem.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="flex-shrink-0">
                            <TraumaCenterButton />
                        </div>
                    </div>
                </nav>
            </header>

            {/* Mobile Menu (Visible when hamburger is clicked) */}
            <div
                className={`md:hidden bg-white bg-opacity-100 overflow-hidden transition-all duration-300 fixed left-0 right-0 z-40 ${isMobileMenuOpen ? "max-h-[90vh] shadow-lg" : "max-h-0"
                    }`}
                style={{ top: '108px' }}
            >
                <div className="flex flex-col px-4 py-4 space-y-3 max-h-[85vh] overflow-y-auto bg-white bg-opacity-100">
                    <Link
                        to="/doctor"
                        onClick={toggleMobileMenu}
                        className={`flex items-center gap-2 font-medium ${location.pathname === "/doctor" ? "text-[#AD3DC5]" : "text-[#009CA6]"
                            }`}
                    >
                        <FaUserMd /> Our Doctors
                    </Link>
                    <Link
                        to="/medical-specialty"
                        onClick={toggleMobileMenu}
                        className={`flex items-center gap-2 font-medium ${location.pathname === "/medical-specialty"
                            ? "text-[#AD3DC5]"
                            : "text-[#009CA6]"
                            }`}
                    >
                        <FaStethoscope /> Medical Specialties
                    </Link>
                    <Link
                        to="/opd-Schedule"
                        onClick={toggleMobileMenu}
                        className={`flex items-center gap-2 font-medium ${location.pathname === "/opd-Schedule"
                            ? "text-[#AD3DC5]"
                            : "text-[#009CA6]"
                            }`}
                    >
                        <Notebook className="h-5" /> OPD Schedule
                    </Link>

                    {/* NEW: Forms Dropdown - Mobile */}
                    <div className="flex flex-col">
                        <button
                            className="flex items-center justify-between w-full font-medium text-[#009CA6] text-left p-2 rounded hover:bg-gray-100"
                            onClick={() => handleClick('forms-mobile')}
                            aria-expanded={openDropdown === 'forms-mobile'}
                            aria-controls="mobile-forms-dropdown"
                        >
                            <span className="flex items-center gap-2">
                                <FileText className="h-5" />
                                फॉर्म / Forms
                            </span>
                            {openDropdown === 'forms-mobile' ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                        </button>
                        <div
                            id="mobile-forms-dropdown"
                            className={`flex flex-col pl-4 mt-2 space-y-2 text-sm text-black overflow-hidden transition-all duration-300 bg-white bg-opacity-100 ${openDropdown === 'forms-mobile' ? "max-h-96" : "max-h-0"
                                }`}
                        >
                            {formsMenuItems.map((formItem, i) => (
                                <Link
                                    key={i}
                                    to={formItem.link}
                                    onClick={toggleMobileMenu}
                                    className={`flex items-start gap-2 py-2 px-2 rounded hover:bg-gray-100 ${location.pathname === formItem.link
                                            ? "font-semibold text-[#004AAC] bg-blue-50"
                                            : ""
                                        }`}
                                >
                                    <formItem.icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-medium">{formItem.title}</div>
                                        <div className="text-xs text-gray-500">{formItem.subtitle}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    <a href="https://kiosk.traumacentrebhu.com" target="_blank" rel="noopener noreferrer">
                        <div className="flex items-center gap-2 text-[#0CA6A6] font-medium">
                            <FaUser /> Patient Portal
                        </div>
                    </a>

                    <Link
                        to="/bed-availability"
                        onClick={toggleMobileMenu}
                        className={`
                            relative inline-flex items-center justify-center
                            w-full px-4 py-3 text-sm
                            ${location.pathname === "/bed-availability"
                                ? "bg-[#AD3DC5]"
                                : isBlinking
                                    ? "bg-gradient-to-r from-red-500 via-pink-500 to-red-600"
                                    : "bg-gradient-to-r from-teal-600 via-teal-600 to-blue-700"
                            }
                            text-white font-semibold rounded-lg shadow-md
                            transform transition-all duration-300 ease-in-out
                            hover:scale-105 hover:shadow-lg active:scale-95
                            cursor-pointer group overflow-hidden
                            border-2 border-white/20
                            ${isBlinking ? 'animate-pulse' : ''}
                        `}
                    >
                        <div className={`absolute inset-0 rounded-lg ${isBlinking ? 'animate-ping' : ''} bg-white opacity-10`}></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent 
                            opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full 
                            transition-all duration-700 ease-out -skew-x-12"></div>
                        <div className="relative flex items-center justify-center gap-1.5 sm:gap-2">
                            <Bed className="h-5" />
                            <span className="font-semibold leading-tight">Bed Availability</span>
                        </div>
                        <div className={`
                            absolute -top-1 -right-1 w-3 h-3
                            bg-yellow-400 rounded-full border-2 border-white
                            ${isBlinking ? 'animate-pulse' : ''}
                        `}></div>
                    </Link>
                    <div className="py-2">
                        <TraumaCenterButton isMobile={true} />
                    </div>
                    <button
                        className="flex items-center gap-2 bg-[#FF4F73] text-white px-4 py-2 rounded-lg shadow hover:bg-red-500 transition w-fit text-sm"
                        onClick={toggleEmergencyModal}
                    >
                        <FaPhoneAlt className="text-white" />
                        <span className="font-semibold">Emergency</span>
                    </button>
                    <div className="flex flex-col gap-4 pt-4 border-t border-gray-200">
                        {menuItems.map((item, index) => (
                            <div key={index} className="flex flex-col">
                                {item.link ? (
                                    <Link
                                        to={item.link}
                                        onClick={toggleMobileMenu}
                                        className={`flex items-center gap-1 font-medium text-black ${location.pathname === item.link
                                            ? "font-bold"
                                            : ""
                                            } p-2 hover:bg-gray-100 rounded`}
                                    >
                                        {item.title}
                                    </Link>
                                ) : (
                                    <button
                                        className="flex items-center justify-between w-full font-medium text-black text-left p-2 rounded hover:bg-gray-100"
                                        onClick={() => handleClick(index)}
                                        aria-expanded={openDropdown === index}
                                        aria-controls={`mobile-dropdown-${index}`}
                                    >
                                        <span>{item.title}</span>
                                        {openDropdown === index ? (
                                            <ChevronUp className="w-4 h-4" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4" />
                                        )}
                                    </button>
                                )}
                                {item.dropdown && (
                                    <div
                                        id={`mobile-dropdown-${index}`}
                                        className={`flex flex-col pl-4 mt-2 space-y-2 text-sm text-black overflow-hidden transition-all duration-300 bg-white bg-opacity-100 ${openDropdown === index ? "max-h-96" : "max-h-0"
                                            }`}
                                    >
                                        {item.dropdown.map((subItem, i) => (
                                            <Link
                                                key={i}
                                                to={subItem.link}
                                                onClick={toggleMobileMenu}
                                                className={`block py-2 px-2 rounded hover:bg-gray-100 ${location.pathname === subItem.link
                                                    ? "font-semibold text-[#004AAC]"
                                                    : ""
                                                    }`}
                                            >
                                                {subItem.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Emergency Modal */}
            {showEmergencyModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-80 md:w-96 overflow-hidden">
                        <div className="bg-[#FF4F73] text-white py-3 px-4 flex justify-between items-center">
                            <h3 className="text-lg font-bold flex items-center">
                                <FaPhoneAlt className="mr-2" /> Emergency Contact
                            </h3>
                            <button
                                onClick={toggleEmergencyModal}
                                className="text-white hover:text-gray-200"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="mb-4">
                                <p className="text-gray-600 mb-1 text-sm">Emergency Helpline:</p>
                                <p className="font-semibold text-lg">{emergencyContact.phone}</p>
                            </div>
                            <div className="mb-6">
                                <p className="text-gray-600 mb-1 text-sm">Email:</p>
                                <p className="font-medium">{emergencyContact.email}</p>
                            </div>
                            <div className="flex justify-between gap-3">
                                <button
                                    onClick={toggleEmergencyModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition w-1/2"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEmergencyCall}
                                    className="px-4 py-2 bg-[#FF4F73] text-white rounded hover:bg-red-500 transition flex items-center justify-center gap-2 w-1/2"
                                >
                                    <FaPhoneAlt /> Call Now
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Spacer to prevent content from being hidden under fixed navbar */}
            <div className="h-[108px] md:h-[144px]"></div>
        </>
    );
};

export default Navbar;